import type { PoConsumption, PurchaseOrder } from '../../../services/purchaseOrderService';
import { purchaseOrderService } from '../../../services/purchaseOrderService';
import type {
  FuelSurchargeLookupRow,
  HiredEquipmentGridRow,
  PurchaseOrderOption,
} from '../types';
import { fuelSurchargePerHourForLine } from './fuelSurchargeLookup';
import { sumAmounts } from './gridMath';

/** Warn when this time card would consume this share of remaining PO balance. */
export const PO_REMAINING_WARNING_THRESHOLD = 0.8;

export interface PoBalanceWarning {
  poId: number;
  poNumber: string;
  remaining: number;
  amount: number;
  entryAmount: number;
  projectedRemaining: number;
  /** entry / remaining when remaining > 0; otherwise null (already at or over). */
  remainingUsedRatio: number | null;
  wouldExceed: boolean;
}

export interface LoadPoBalanceWarningsInput {
  rows: HiredEquipmentGridRow[];
  lockedPoId?: number | null;
  purchaseOrders?: PurchaseOrderOption[];
  fuelSurcharges?: FuelSurchargeLookupRow[];
}

export function resolveRowPoId(
  row: Pick<HiredEquipmentGridRow, 'poId' | 'poNumber'>,
  lockedPoId?: number | null,
  purchaseOrders: PurchaseOrderOption[] = [],
): number | null {
  if (lockedPoId != null) return lockedPoId;
  if (row.poId != null) return row.poId;
  const poNumber = row.poNumber?.trim();
  if (!poNumber) return null;
  return purchaseOrders.find((po) => po.poNumber === poNumber)?.id ?? null;
}

export function entryTotalsByPoId(
  rows: HiredEquipmentGridRow[],
  lockedPoId?: number | null,
  purchaseOrders: PurchaseOrderOption[] = [],
  fuelSurcharges: FuelSurchargeLookupRow[] = [],
): Map<number, number> {
  const totals = new Map<number, number>();
  const grouped = new Map<number, HiredEquipmentGridRow[]>();
  for (const row of rows) {
    const poId = resolveRowPoId(row, lockedPoId, purchaseOrders);
    if (poId == null) continue;
    const list = grouped.get(poId) ?? [];
    list.push(row);
    grouped.set(poId, list);
  }
  for (const [poId, group] of grouped) {
    const withFuel = group.map((row) => ({
      ...row,
      fuelPerHour: fuelSurchargePerHourForLine(fuelSurcharges, row),
    }));
    totals.set(poId, sumAmounts(withFuel));
  }
  return totals;
}

/**
 * Warn when this entry would consume >= 80% of remaining, or would go over remaining.
 * Remaining already at/below zero also warns for any additional spend.
 */
export function warningForPoRemaining(
  po: { id: number; poNumber: string; amount: number; consumption?: PoConsumption | null },
  entryAmount: number,
  threshold = PO_REMAINING_WARNING_THRESHOLD,
): PoBalanceWarning | null {
  if (!(entryAmount > 0)) return null;
  const consumption = po.consumption;
  if (!consumption || consumption.pending) return null;

  const remaining = consumption.remaining;
  const wouldExceed = entryAmount > remaining;
  const remainingUsedRatio = remaining > 0 ? entryAmount / remaining : null;
  const hitsThreshold = remaining <= 0 || (remainingUsedRatio != null && remainingUsedRatio >= threshold);
  if (!hitsThreshold) return null;

  return {
    poId: po.id,
    poNumber: po.poNumber,
    remaining,
    amount: consumption.amount,
    entryAmount,
    projectedRemaining: remaining - entryAmount,
    remainingUsedRatio,
    wouldExceed,
  };
}

export async function loadPoBalanceWarnings(
  input: LoadPoBalanceWarningsInput,
  getPo: (id: number) => Promise<PurchaseOrder> = (id) => purchaseOrderService.getPurchaseOrderById(id),
): Promise<PoBalanceWarning[]> {
  const totals = entryTotalsByPoId(
    input.rows,
    input.lockedPoId,
    input.purchaseOrders,
    input.fuelSurcharges,
  );
  if (totals.size === 0) return [];

  const settled = await Promise.allSettled(
    [...totals.keys()].map(async (poId) => {
      const po = await getPo(poId);
      return warningForPoRemaining(po, totals.get(poId) ?? 0);
    }),
  );

  const warnings: PoBalanceWarning[] = [];
  for (const result of settled) {
    if (result.status === 'rejected') {
      console.error('Failed to load purchase order remaining balance', result.reason);
      continue;
    }
    if (result.value) warnings.push(result.value);
  }
  return warnings;
}
